import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MarketingNav from "@/components/app/MarketingNav";
import OtterIcon from "@/components/app/OtterIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles, ArrowRight, X, Loader2, Check, Plus,
} from "lucide-react";

/**
 * /quick-start — SMB onboarding.
 *
 * Three steps in one page:
 *   1. What's your business?  → dropdown of 20 curated business types
 *   2. Where are you?         → city (+ optional neighborhood)
 *   3. Review the keywords    → editable chips, accept all or refine
 *
 * On submit:
 *   - If user is logged in → POST /api/quick-start creates project + tracks keywords → redirect to /app
 *   - If not logged in     → stash the payload in sessionStorage + redirect to /register
 *                            (the register flow auto-resumes on first dashboard load)
 */

const PRIORITY_TONE = {
  high: "border-foreground bg-foreground text-background",
  medium: "border-border bg-secondary text-foreground",
  low: "border-border bg-white text-muted-foreground",
};

const INTENT_LABEL = {
  commercial: "buyer",
  informational: "research",
  navigational: "branded",
};

export default function QuickStart() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [businessTypes, setBusinessTypes] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [domain, setDomain] = useState("");
  const [projectName, setProjectName] = useState("");
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [resolved, setResolved] = useState([]);     // [{keyword, intent, priority, _enabled}]
  const [customKw, setCustomKw] = useState("");
  const [resolving, setResolving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load business types once.
  useEffect(() => {
    document.title = "Quick start — ArloSEO";
    (async () => {
      try {
        const { data } = await api.get("/keyword-templates");
        setBusinessTypes(data.business_types || []);
      } catch {
        toast.error("Couldn't load business types. Refresh and try again.");
      }
    })();
  }, []);

  // Group business types by category for the dropdown.
  const grouped = useMemo(() => {
    const map = new Map();
    businessTypes.forEach((b) => {
      if (!map.has(b.category_label)) map.set(b.category_label, []);
      map.get(b.category_label).push(b);
    });
    return Array.from(map.entries());
  }, [businessTypes]);

  const selected = useMemo(
    () => businessTypes.find((b) => b.slug === selectedSlug) || null,
    [businessTypes, selectedSlug],
  );

  // Auto-resolve keywords whenever slug + city change.
  useEffect(() => {
    if (!selectedSlug || !city.trim()) {
      setResolved([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setResolving(true);
      try {
        const { data } = await api.post(
          `/keyword-templates/${selectedSlug}/resolve`,
          { city: city.trim(), neighborhood: neighborhood.trim() },
        );
        if (cancelled) return;
        setResolved((data.keywords || []).map((k) => ({ ...k, _enabled: true })));
      } catch {
        if (!cancelled) setResolved([]);
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSlug, city, neighborhood]);

  function toggleKeyword(idx) {
    setResolved((prev) => prev.map((k, i) => (i === idx ? { ...k, _enabled: !k._enabled } : k)));
  }

  function addCustom() {
    const kw = customKw.trim().toLowerCase();
    if (!kw) return;
    if (resolved.some((k) => k.keyword === kw)) {
      toast.message("Already in the list");
      setCustomKw("");
      return;
    }
    setResolved((prev) => [...prev, { keyword: kw, intent: "commercial", priority: "medium", _enabled: true, _custom: true }]);
    setCustomKw("");
  }

  const enabledKeywords = resolved.filter((k) => k._enabled);

  async function submit(e) {
    e?.preventDefault?.();
    if (!selectedSlug) {
      toast.error("Pick your business type first.");
      return;
    }
    if (!domain.trim() || !projectName.trim() || !city.trim()) {
      toast.error("Fill in your website, business name, and city.");
      return;
    }
    if (enabledKeywords.length === 0) {
      toast.error("Pick at least one keyword to track.");
      return;
    }

    const payload = {
      business_slug: selectedSlug,
      project_name: projectName.trim(),
      project_url: domain.trim(),
      city: city.trim(),
      neighborhood: neighborhood.trim(),
      keywords: enabledKeywords.map((k) => k.keyword),
    };

    // Not logged in → stash + redirect to register.
    if (!user || typeof user !== "object") {
      try {
        sessionStorage.setItem("arloseo_quick_start_pending", JSON.stringify(payload));
      } catch { /* ignore */ }
      toast.message("Almost there", { description: "Create a free account to save your project + keywords." });
      navigate("/register?from=quick-start");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await api.post("/quick-start", payload);
      toast.success(`Tracking ${data.keywords_tracked} keyword${data.keywords_tracked === 1 ? "" : "s"} for ${data.project_name}.`);
      // Clear any pending stash and land on the project.
      try { sessionStorage.removeItem("arloseo_quick_start_pending"); } catch { /* ignore */ }
      navigate(`/app/projects/${data.project_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="quick-start-page">
        {/* Header */}
        <div className="border-b border-border pb-10">
          <div className="flex items-center gap-2 font-mono-data text-xs uppercase tracking-widest text-muted-foreground">
            <OtterIcon size={16} className="text-foreground" />
            // 90-second setup · no SEO knowledge required
          </div>
          <h1 className="font-semibold tracking-tight mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Tell Arlo what you do —{" "}
            <span className="bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">
              he picks the keywords.
            </span>
          </h1>
          <p className="mt-4 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
            Pick your business type, drop in your city, and Arlo populates the queries that actually drive customers in your area. Skip the SEO research — that&apos;s what we&apos;re here for.
          </p>
        </div>

        <form className="mt-12 space-y-12" onSubmit={submit}>
          {/* Step 1 — Business */}
          <section data-testid="quick-start-step-business">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono-data text-xs text-muted-foreground">01</span>
              <h2 className="font-semibold tracking-tight text-xl">What do you do?</h2>
            </div>
            <Label htmlFor="business" className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
              Pick the closest match
            </Label>
            <select
              id="business"
              data-testid="quick-start-business-select"
              value={selectedSlug}
              onChange={(e) => setSelectedSlug(e.target.value)}
              className="mt-2 w-full border border-foreground bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-foreground/20"
              required
            >
              <option value="">— Choose your business type —</option>
              {grouped.map(([cat, items]) => (
                <optgroup key={cat} label={cat}>
                  {items.map((b) => (
                    <option key={b.slug} value={b.slug}>{b.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {selected && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="quick-start-business-hint">
                {selected.description} · Arlo has {selected.keyword_count} curated keywords ready for this type.
              </p>
            )}
          </section>

          {/* Step 2 — Where */}
          <section data-testid="quick-start-step-where">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono-data text-xs text-muted-foreground">02</span>
              <h2 className="font-semibold tracking-tight text-xl">Where are you?</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city" className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
                  City or metro <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="city"
                  data-testid="quick-start-city"
                  className="mt-2 rounded-none border-foreground"
                  placeholder="e.g. Austin"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="neighborhood" className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
                  Neighborhood (optional)
                </Label>
                <Input
                  id="neighborhood"
                  data-testid="quick-start-neighborhood"
                  className="mt-2 rounded-none border-foreground"
                  placeholder="e.g. East Austin"
                  value={neighborhood}
                  onChange={(e) => setNeighborhood(e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Step 3 — Site basics */}
          <section data-testid="quick-start-step-site">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono-data text-xs text-muted-foreground">03</span>
              <h2 className="font-semibold tracking-tight text-xl">Your website</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="project-name" className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
                  Business name <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="project-name"
                  data-testid="quick-start-project-name"
                  className="mt-2 rounded-none border-foreground"
                  placeholder="Acme Tattoo Co."
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="domain" className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
                  Website URL <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="domain"
                  data-testid="quick-start-domain"
                  className="mt-2 rounded-none border-foreground"
                  placeholder="https://acmetattoo.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  required
                />
              </div>
            </div>
          </section>

          {/* Step 4 — Keywords */}
          <section data-testid="quick-start-step-keywords">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-mono-data text-xs text-muted-foreground">04</span>
              <h2 className="font-semibold tracking-tight text-xl">
                Arlo&apos;s keyword picks
                {enabledKeywords.length > 0 && (
                  <span className="ml-3 font-mono-data text-xs text-muted-foreground">
                    {enabledKeywords.length} selected
                  </span>
                )}
              </h2>
            </div>

            {!selectedSlug || !city.trim() ? (
              <div className="border border-dashed border-foreground/30 p-8 text-center text-sm text-muted-foreground" data-testid="quick-start-keywords-empty">
                Pick a business type and add your city above — Arlo will populate the keywords automatically.
              </div>
            ) : resolving ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="quick-start-keywords-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Picking keywords for you…
              </div>
            ) : resolved.length === 0 ? (
              <div className="border border-dashed border-foreground/30 p-8 text-center text-sm text-muted-foreground">
                No keywords resolved. Try a different city.
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-4">
                  Tap any chip to add or remove it. Each tracked keyword gets weekly rank checks + opportunity scoring.
                </p>
                <div className="flex flex-wrap gap-2" data-testid="quick-start-keywords-chips">
                  {resolved.map((k, idx) => (
                    <button
                      key={k.keyword}
                      type="button"
                      onClick={() => toggleKeyword(idx)}
                      data-testid={`quick-start-keyword-chip-${idx}`}
                      data-enabled={k._enabled ? "true" : "false"}
                      className={
                        "group inline-flex items-center gap-2 px-3 py-1.5 border text-sm transition-colors " +
                        (k._enabled
                          ? PRIORITY_TONE[k.priority] || PRIORITY_TONE.medium
                          : "border-dashed border-border bg-white text-muted-foreground line-through")
                      }
                    >
                      {k._enabled ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                      <span>{k.keyword}</span>
                      <span className="font-mono-data text-[9px] uppercase tracking-widest opacity-60">
                        {INTENT_LABEL[k.intent] || k.intent}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Add custom */}
                <div className="mt-6 flex items-center gap-2 max-w-md" data-testid="quick-start-custom-add">
                  <Input
                    className="rounded-none border-border"
                    placeholder="Add your own keyword (optional)"
                    value={customKw}
                    onChange={(e) => setCustomKw(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
                    data-testid="quick-start-custom-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-none"
                    onClick={addCustom}
                    disabled={!customKw.trim()}
                    data-testid="quick-start-custom-add-button"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
          </section>

          {/* Submit */}
          <section className="border-t border-border pt-10 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="quick-start-cancel">
              <X className="w-3.5 h-3.5" /> Cancel
            </Link>
            <Button
              type="submit"
              size="lg"
              disabled={submitting || enabledKeywords.length === 0}
              className="rounded-none bg-foreground hover:bg-foreground/90 px-8"
              data-testid="quick-start-submit"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {user && typeof user === "object"
                ? `Track ${enabledKeywords.length} keyword${enabledKeywords.length === 1 ? "" : "s"} →`
                : `Continue with ${enabledKeywords.length} keyword${enabledKeywords.length === 1 ? "" : "s"} →`}
            </Button>
          </section>
        </form>

        {/* Reassurance footer */}
        <div className="mt-16 border border-foreground bg-foreground text-background p-6 sm:p-8 relative overflow-hidden" data-testid="quick-start-reassurance">
          <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
          <div className="relative grid sm:grid-cols-3 gap-6 text-sm">
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-background/60">// 01</div>
              <div className="mt-2 font-semibold tracking-tight text-lg leading-snug">No card needed</div>
              <div className="mt-1 text-background/70">14-day Pro trial. Cancel anytime.</div>
            </div>
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-background/60">// 02</div>
              <div className="mt-2 font-semibold tracking-tight text-lg leading-snug">Arlo handles the SEO</div>
              <div className="mt-1 text-background/70">Weekly audits, ranking checks, content briefs.</div>
            </div>
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-background/60">// 03</div>
              <div className="mt-2 font-semibold tracking-tight text-lg leading-snug">Built for non-techies</div>
              <div className="mt-1 text-background/70">If you can pick from a list, you can rank.</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
